import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'

import PageTitle from '../../../components/page-title'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LoadingButton from '@mui/lab/LoadingButton'

import {
  useGetImportQuery,
  usePutImportFieldsMutation,
  usePutImportStatusMutation,
  useDeleteImportMutation,
  usePostImportMutation,
} from '../slice'

import ImportErrors from './importErrors'
import MapFields from './mapFields'
import PreviewImport from './previewImport'
import DeleteImport from './deleteImport'
import PostImport from './postImport'

const ImportDetails = () => {
  const [action, setAction] = useState()
  const [saveFields, setSaveFields] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showImportDialog, setShowImportDialog] = useState(false)
  const { importId } = useParams()

  const { data: imported, isLoading } = useGetImportQuery({ importId })
  const [putImportStatus, { isLoading: isUpdating }] = usePutImportStatusMutation()
  const [{ isLoading: isDeleting }] = useDeleteImportMutation()
  const [{ isLoading: isSaving }] = usePutImportFieldsMutation()
  const [{ isLoading: isImporting }] = usePostImportMutation()

  const handleRemapClick = useCallback(() => {
    if (imported) {
      putImportStatus({ importId: imported.id, payload: { status: 'mapping' } })
    }
  }, [imported, putImportStatus])

  const handleImportClick = useCallback(() => {
    if (imported) {
      setShowImportDialog(true)
    }
  }, [imported])

  const handleDeleteClick = useCallback(() => {
    setShowDeleteDialog(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imported])

  const handleSaveClick = useCallback(() => {
    setSaveFields(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imported])

  useEffect(() => {
    if (imported) {
      const renderAction = () => {
        switch (imported.status) {
          case 'error':
            return (
              <LoadingButton
                loading={isDeleting}
                sx={{ ml: 1 }}
                variant="outlined"
                color="error"
                onClick={handleDeleteClick}
              >
                Delete
              </LoadingButton>
            )
          case 'mapping':
            return (
              <>
                <LoadingButton sx={{ ml: 1 }} loading={isSaving} variant="outlined" onClick={handleSaveClick}>
                  Save
                </LoadingButton>
                <LoadingButton
                  loading={isDeleting}
                  sx={{ ml: 1 }}
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteClick}
                >
                  Delete
                </LoadingButton>
              </>
            )
          case 'mapped':
            return (
              <>
                <LoadingButton sx={{ ml: 1 }} loading={isUpdating} variant="outlined" onClick={handleRemapClick}>
                  Remap
                </LoadingButton>
                <LoadingButton sx={{ ml: 1 }} loading={isImporting} variant="outlined" onClick={handleImportClick}>
                  Import
                </LoadingButton>
                <LoadingButton
                  loading={isDeleting}
                  sx={{ ml: 1 }}
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteClick}
                >
                  Delete
                </LoadingButton>
              </>
            )
          default:
            return []
        }
      }

      setAction(renderAction())
    }
  }, [
    imported,
    isDeleting,
    isSaving,
    isUpdating,
    isImporting,
    handleDeleteClick,
    handleImportClick,
    handleSaveClick,
    handleRemapClick,
  ])

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitle title={`Prospects :: Imports :: ${imported?.filename || '...'}`} action={action} />
      <Box margin={2}>
        {isLoading && <Typography>Loading...</Typography>}
        {imported && (
          <>
            <Typography>Filename: {imported.filename}</Typography>
            <Typography>Status: {imported.status}</Typography>
            {imported.errors && <ImportErrors errors={imported.errors} />}
            {imported.status === 'mapping' && (
              <MapFields imported={imported} saveFields={saveFields} setSaveFields={setSaveFields} />
            )}
            {imported.status === 'mapped' && <PreviewImport imported={imported} />}
            <DeleteImport imported={imported} open={showDeleteDialog} setOpen={setShowDeleteDialog} />
            <PostImport imported={imported} open={showImportDialog} setOpen={setShowImportDialog} />
          </>
        )}
      </Box>
    </Box>
  )
}

export default ImportDetails
